import { useMemo } from 'react';
import { useSchedule } from '../../../entities/schedule';
import { buildWeekRows } from './buildWeekRows';
import type { WeekRowModel } from './weekTypes';

/**
 * The week grid's one data hook — the same `GET /schedule` the day board makes, over a five-day
 * window instead of one.
 *
 * `technicianIds` stays omitted for the same reason as on the day board: the server then returns
 * the full roster, names and avatars included, so there is no second request for a technician list.
 * Break projection is range-aware server-side, so one projected Pause per working day arrives
 * without any client-side expansion.
 *
 * `scheduleKeys.range` keys on the window, so this is a different cache entry from the day board's
 * — a Tag ↔ Woche toggle costs one fetch. Slicing the week payload into the day board instead is
 * not worth it: the day board's exclusion filter and optimistic patching both assume its own key.
 */
export function useDispositionWeekBoard(
  days: string[],
  from: string,
  to: string,
  todayIso: string,
): {
  rows: WeekRowModel[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const query = useSchedule({ from, to, includeWorkOrder: true });

  const rows = useMemo(
    () => buildWeekRows(query.data, days, todayIso),
    [query.data, days, todayIso],
  );

  return {
    rows,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}
